import { useNavigate } from "@tanstack/react-router";
import { Check, ChevronDown, Plus, Settings } from "lucide-react";
import { useState } from "react";
import { ROUTES } from "@/app/routes";
import { CreateWorkspaceDialog } from "@/features/identity/components/create-workspace-dialog";
import { useAuth } from "@/shared/auth/auth.context";
import { canDo, Permission } from "@/shared/auth/permissions";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { UsageMeter } from "@/shared/entitlements/components/usage-meter";
import { resolvePlanLabel } from "@/shared/entitlements/entitlements.constants";
import { useCredits } from "@/shared/entitlements/hooks/use-credits";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";
import { useSidebarCollapsed } from "@/shared/hooks/use-sidebar-collapsed";
import { cn } from "@/shared/lib/cn";

/**
 * Workspace switcher trigger + dropdown.
 *
 * Reads workspaces from useAuth() (real backend data when authenticated; mock
 * fallback for anonymous dev sessions). Each workspace's `plan` comes from
 * useEntitlements() (see use-active-workspace.tsx), the real plan the caller's
 * account is on, resolved to a display label via resolvePlanLabel(). Below
 * the switcher, a compact AI-credit UsageMeter sits outside the dropdown
 * trigger button on purpose: nesting a progress meter's text inside the
 * trigger would fold its value into the button's accessible name. Footer
 * items: "Create workspace" gated by ACCOUNT_CREATE_WORKSPACE; "Workspace
 * settings" gated by WORKSPACE_EDIT_SETTINGS.
 */
export function WorkspaceSwitcher() {
  const { workspace: active, workspaces, setWorkspace } = useActiveWorkspace();
  const { collapsed } = useSidebarCollapsed();
  const auth = useAuth();
  const credits = useCredits();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const canCreate = canDo(Permission.ACCOUNT_CREATE_WORKSPACE, {
    accountRole: auth.user?.accountRole ?? null,
    workspaceRoles: auth.memberships.map((m) => m.role),
  });
  const canEditSettings = canDo(Permission.WORKSPACE_EDIT_SETTINGS, {
    accountRole: auth.user?.accountRole ?? null,
    workspaceRoles: auth.memberships.map((m) => m.role),
  });

  return (
    <div className={cn("pb-2.5 pt-3.5", collapsed ? "px-2.5" : "px-3")}>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={collapsed ? `Workspace: ${active.name}` : undefined}
          title={collapsed ? active.name : undefined}
          className={cn(
            "group items-center rounded-lg",
            "border border-border bg-card text-left",
            "transition-[border-color,box-shadow] duration-150 ease-out",
            "hover:border-border-strong hover:shadow-xs",
            "data-[state=open]:border-violet-300 data-[state=open]:shadow-ring-focus",
            "outline-none focus-visible:shadow-ring-focus",
            collapsed
              ? "grid size-8 place-items-center p-0"
              : "grid w-full grid-cols-[32px_1fr_14px] gap-2.5 px-2.5 py-2",
          )}
        >
          <WorkspaceMark mark={active.mark} background={active.markBackground} size="md" />
          {collapsed ? null : (
            <>
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-sm font-semibold tracking-tight">{active.name}</span>
                {/* No plan badge here: which plan the account is on is a
                    billing fact, and it lives on the billing screen (reached
                    from Settings) rather than being pinned permanently
                    beside the workspace name, where it competed with the
                    workspace's own identity and pushed the member count into
                    a truncated second line. The switcher below still shows
                    each workspace's plan, where it genuinely distinguishes
                    one workspace from another. */}
                <span className="truncate font-mono text-3xs text-text-tertiary">
                  {active.memberCount} members
                </span>
              </div>
              <Chevron />
            </>
          )}
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          sideOffset={6}
          className="w-[var(--radix-dropdown-menu-trigger-width)] p-1.5"
        >
          <DropdownMenuLabel className="px-2.5 pb-1 pt-2 font-mono text-3xs uppercase tracking-wider text-text-quaternary">
            Switch workspace
          </DropdownMenuLabel>

          {workspaces.map((ws) => (
            <DropdownMenuItem
              key={ws.id}
              onSelect={() => setWorkspace(ws.id)}
              className="grid grid-cols-[24px_1fr_14px] items-center gap-2.5 px-2 py-1.5"
            >
              <WorkspaceMark mark={ws.mark} background={ws.markBackground} size="sm" />
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-medium">{ws.name}</span>
                <span className="truncate font-mono text-3xs tracking-wide text-text-tertiary">
                  {resolvePlanLabel(ws.plan)} · {ws.memberCount}{" "}
                  {ws.memberCount === 1 ? "person" : "people"}
                </span>
              </div>
              {ws.id === active.id ? <Check className="size-3.5 text-text-brand" /> : <span />}
            </DropdownMenuItem>
          ))}

          <DropdownMenuSeparator className="my-1" />

          {canCreate ? (
            <DropdownMenuItem
              onSelect={() => setCreateOpen(true)}
              className="gap-2.5 px-2 py-1.5 text-xs text-text-secondary"
            >
              <Plus className="size-3.5 text-text-tertiary" />
              Create workspace
            </DropdownMenuItem>
          ) : null}
          {canEditSettings ? (
            <DropdownMenuItem
              onSelect={() =>
                navigate({
                  to: ROUTES.workspace.settings.path,
                  params: { workspaceSlug: active.slug },
                })
              }
              className="gap-2.5 px-2 py-1.5 text-xs text-text-secondary"
            >
              <Settings className="size-3.5 text-text-tertiary" />
              Workspace settings
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {collapsed ? null : (
        <UsageMeter
          label="AI credits"
          used={credits.used}
          total={credits.grant}
          size="sm"
          className="mt-2"
        />
      )}
      <CreateWorkspaceDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

/**
 * Workspace gradient mark, a small square avatar-like element. Uses inline `style`
 * for `background` because each workspace carries a unique CSS gradient string.
 * reason: dynamic per-workspace gradient, values come from data, not the token system.
 */
function WorkspaceMark({
  mark,
  background,
  size,
}: {
  mark: string;
  background: string;
  size: "sm" | "md";
}) {
  return (
    <span
      style={{ background }}
      className={cn(
        "relative grid place-items-center overflow-hidden rounded-md text-white font-semibold",
        size === "md" ? "size-8 text-sm shadow-brand" : "size-6 text-3xs",
      )}
    >
      {mark}
      <span
        aria-hidden
        className="absolute inset-0 [background:radial-gradient(60%_70%_at_30%_20%,rgba(255,255,255,0.35),transparent_60%)]"
      />
    </span>
  );
}

function Chevron() {
  return (
    <ChevronDown
      aria-hidden
      className="size-3.5 text-text-tertiary transition-transform duration-150 group-data-[state=open]:rotate-180"
    />
  );
}
